import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { COMPANY_INFO, SEO_CONFIG } from '../data/companyData';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';

export default function AboutPage() {
  const techImg = 'https://assets.auxodev.dk/assets/9badcd2d-12ea-450b-8280-42a177052604/om-os-tekniker.jpg';
  const installationImg = 'https://assets.auxodev.dk/assets/9badcd2d-12ea-450b-8280-42a177052604/hero-climate-refrigeration.jpg';

  return (
    <>
      <SEOHead
        title={SEO_CONFIG.about.title}
        description={SEO_CONFIG.about.description}
        image={techImg}
      />

      {/* Hero Header */}
      <section className="bg-neutral-950 text-white py-16 sm:py-20 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-[#DFBF77] text-xs font-semibold uppercase tracking-wider">
              <i className="fa-solid fa-building-user" aria-hidden="true" />
              Om virksomheden
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Faglig ekspertise inden for <span className="text-[#DFBF77]">køl, klima og energioptimering</span>
            </h1>
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              Morine Køl & Klimateknik er din pålidelige partner inden for professionelle køle- og klimaanlæg. Vi rådgiver og leverer driftssikre løsninger til erhverv, detailhandel og industri med udgangspunkt i Hillerød.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section: Faglig tilgang og værdier */}
      <section className="py-16 sm:py-20 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <span className="inline-block text-xs font-bold text-[#8D6D2F] uppercase tracking-wider bg-[#FAF6ED] px-3.5 py-1.5 rounded-full border border-[#EAD5A5] shadow-xs">
                  Vores faglige grundlag
                </span>
                <h2 className="mt-3.5 pt-1 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-neutral-950">
                  Teknisk præcision og fokus på stabil drift
                </h2>
              </div>
              <p className="text-neutral-700 text-base sm:text-lg leading-relaxed">
                Hos Morine Køl & Klimateknik arbejder vi ud fra princippet om, at en køle- eller klimaløsning skal være driftsikker fra dag ét og designet til at holde energiforbruget på et absolut minimum.
              </p>
              <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
                Vi lægger stor vægt på personlig kontakt og direkte dialog med vores kunder. Det sikrer, at alle tekniske specifikationer, temperaturkrav og driftsmønstre afdækkes grundigt, inden anlægget dimensioneres og installeres.
              </p>

              {/* Core Pillars (Non-clickable, stable) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/80 space-y-1.5">
                  <div className="flex items-center gap-2 text-[#8D6D2F] font-bold text-sm">
                    <i className="fa-solid fa-comments-dollar text-xs" aria-hidden="true" />
                    <span>Professionel rådgivning</span>
                  </div>
                  <p className="text-xs text-neutral-600">
                    Klar teknisk vejledning uden unødige mellemled, så du får den rette kapacitet.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/80 space-y-1.5">
                  <div className="flex items-center gap-2 text-[#8D6D2F] font-bold text-sm">
                    <i className="fa-solid fa-bolt text-xs" aria-hidden="true" />
                    <span>Energioptimering</span>
                  </div>
                  <p className="text-xs text-neutral-600">
                    Fokus på optimal virkningsgrad, reduceret elregning og fremtidssikrede anlæg.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/80 space-y-1.5">
                  <div className="flex items-center gap-2 text-[#8D6D2F] font-bold text-sm">
                    <i className="fa-solid fa-shield-check text-xs" aria-hidden="true" />
                    <span>Driftssikkerhed</span>
                  </div>
                  <p className="text-xs text-neutral-600">
                    Kvalitetskomponenter og solidt håndværk, der minimerer risikoen for driftsstop.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-neutral-50 border border-neutral-200/80 space-y-1.5">
                  <div className="flex items-center gap-2 text-[#8D6D2F] font-bold text-sm">
                    <i className="fa-solid fa-handshake text-xs" aria-hidden="true" />
                    <span>Personlig kontakt</span>
                  </div>
                  <p className="text-xs text-neutral-600">
                    Tæt opfølgning og hurtig respons ved forespørgsler og servicebehov.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-5 space-y-4">
              <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-lg aspect-[4/3] bg-neutral-900">
                <img
                  src={getOptimizedImageUrl(techImg, { width: 800, height: 600 })}
                  alt="Morine Køl & Klimateknik tekniker"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Fact snippet */}
              <div className="p-5 rounded-xl bg-[#FAF6ED] border border-[#EAD5A5] text-neutral-900 text-xs space-y-1.5">
                <div className="font-bold text-[#8D6D2F] uppercase tracking-wider">
                  Virksomhedsinformation
                </div>
                <div className="text-neutral-700">
                  <strong>{COMPANY_INFO.name}</strong> • CVR: {COMPANY_INFO.cvr}
                </div>
                <div className="text-neutral-600">
                  {COMPANY_INFO.address.full}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Arbejdsområder & Brancher */}
      <section className="py-16 sm:py-20 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block text-xs font-bold text-[#8D6D2F] uppercase tracking-wider bg-[#FAF6ED] px-3.5 py-1.5 rounded-full border border-[#EAD5A5] shadow-xs">
              Målgrupper
            </span>
            <h2 className="mt-3.5 pt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-950">
              Hvem hjælper vi?
            </h2>
            <p className="mt-2 text-sm sm:text-base text-neutral-600">
              Vores løsninger er målrettet virksomheder med professionelle køle- og klimakrav.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-white border border-neutral-200 shadow-xs space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base">
                <i className="fa-solid fa-store" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900">Detail & Fødevarer</h3>
              <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                Supermarkeder, bagere, delikatesse, restauranter og convenience-butikker med behov for præcis køl og frost.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-neutral-200 shadow-xs space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base">
                <i className="fa-solid fa-industry" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900">Industri & Produktion</h3>
              <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                Fabrikker, procesanlæg, lagerhaller og distributionscentre med høje krav til konstant drift og redundans.
              </p>
            </div>

            <div className="sm:col-span-2 lg:col-span-1 p-6 rounded-2xl bg-white border border-neutral-200 shadow-xs space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#FAF6ED] border border-[#EAD5A5] text-[#8D6D2F] flex items-center justify-center text-base">
                <i className="fa-solid fa-building" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900">Kontor & Erhvervsbygninger</h3>
              <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                Virksomheder og ejendomme, der ønsker behageligt indeklima, serverrumskøling og effektive varmepumper.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
